import { useMutation, useQuery } from "@apollo/client";
import AttachFile from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import Delete from "@mui/icons-material/Delete";
import Download from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import Visibility from "@mui/icons-material/Visibility";
import {
  Alert,
  Button,
  Chip,
  IconButton,
  Link,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import { Box, Grid, Stack } from "@mui/system";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { DisplayFieldWithTooltip } from "components/DisplayFieldWithTooltip";
import {
  AddEditDocumentDialog,
  AddEditDocumentDialogState,
  defaultAddEditDocumentDialogState,
  EditMode,
  FormState,
} from "components/Document/AddEditDocumentDialog";
import { isPreviewable } from "components/Document/shared/fileTypeUtils";
import { FullScreenDocumentPreview } from "components/Document/shared/FullScreenDocumentPreview";
import { InfoBox } from "components/InfoBox";
import { ValueWithStaffEditedNotation } from "components/ValueWithStaffEditedNotation";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { formatAddress } from "encamp-shared/src/utils/address";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { DocumentType, ProcessedManifestsQuery, SubmissionStatus } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useDocumentDownload } from "hooks/useDocumentDownload";
import { useDocumentDownloadLink } from "hooks/useDocumentDownloadLink";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useProcessedManifestSuspense } from "hooks/useProcessedManifest";
import { useTenant } from "hooks/useTenant";
import { useWasteLinesUnified } from "hooks/useWasteLinesUnified";
import { PropsWithChildren, useCallback, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { documentTypeToLabel } from "util/constants";
import { getDaysInTransitInfo } from "util/manifest";

import { ManifestLineItemDetailSectionSkeleton } from "../ManifestLineItems/ManifestLineItemDetailSection";
import {
  ATTACH_DOCUMENT_TO_MANIFEST,
  CREATE_EXCEPTION_REPORT_SUBMISSION,
  DETACH_DOCUMENT_FROM_MANIFEST,
  PROCESSED_MANIFEST_DOCUMENTS_QUERY,
  PROCESSED_MANIFESTS_QUERY,
} from "./api";
import {
  BULK_MANIFEST_DISCREPANCIES_QUERY,
  DiscrepancyReviewDialog,
} from "./DiscrepancyReviewDialog";
import { EditManifestDialog } from "./EditManifestDialog";
import { ManifestDocumentActions } from "./ManifestDocumentActions";
import { ProofOfContactDialog } from "./ProofOfContactDialog";

type TransporterRow = NonNullable<
  ProcessedManifestsQuery["processedManifests"]["items"][number]["wasteTransporters"]
>[number];

const WASTE_DOCUMENT_TYPES = new Set<DocumentType>([
  DocumentType.WasteTransporterContactProof,
  DocumentType.WasteExceptionReportProof,
]);

const transporterColumns: CustomGridColDef<TransporterRow>[] = [
  {
    field: "transporterLineNumber",
    headerName: "#",
    width: 50,
  },
  {
    field: "transporterName",
    headerName: "Name",
    flex: 1,
    minWidth: 120,
  },
  {
    field: "transporterId",
    headerName: "EPA ID",
    width: 130,
  },
  {
    field: "daysInPossession",
    headerName: "Days in Possession",
    width: 140,
  },
  {
    field: "address",
    headerName: "Address",
    flex: 1,
    minWidth: 150,
    valueGetter: (_value, row) =>
      formatAddress(
        [row.vendorAddressLine1, row.vendorAddressLine2].filter(Boolean).join(" "),
        row.vendorCity,
        row.vendorState,
        row.vendorZip
      ),
  },
];

export interface ManifestDetailSectionProps extends PropsWithChildren {
  processedManifestId: string | undefined;
  onClose: () => void;
  onManifestIdChange?: (newManifestId: string) => void;
}

export const ManifestDetailSection = ({
  processedManifestId,
  onClose,
  children,
}: ManifestDetailSectionProps) => {
  const { tenantId } = useTenant();
  const { featureFlags } = useFeatureFlags();
  const theme = useTheme();
  const alerts = useAlerts();
  const { processedManifest: manifest } = useProcessedManifestSuspense(processedManifestId);
  const { data: wasteLinesUnified } = useWasteLinesUnified(
    {
      search: `tenantId:${tenantId} processedManifestId:${processedManifestId}`,
    },
    { skip: !processedManifestId || !tenantId }
  );
  const { isStaff } = useCurrentUser();
  const showDiscrepancyCounts = isStaff && featureFlags[FeatureFlagKey.WasteManifestDiscrepancies];
  const showCompliance =
    showDiscrepancyCounts && featureFlags[FeatureFlagKey.WasteManifestCompliance];
  const showExceptionReporting =
    showCompliance && featureFlags[FeatureFlagKey.WasteExceptionReporting];
  const showSourceDocument = isStaff && !!featureFlags[FeatureFlagKey.ManifestDocumentIngest];
  const { fetchAndHandleDownload, clearDownloadLink } = useDocumentDownload();
  const { data: documentsData, error: documentsError } = useQuery(
    PROCESSED_MANIFEST_DOCUMENTS_QUERY,
    {
      variables: {
        search: `tenantId:${tenantId} id:${processedManifestId}`,
      },
      skip: !tenantId || !processedManifestId || !showDiscrepancyCounts,
    }
  );
  const manifestDocuments = documentsData?.processedManifests?.items?.[0]?.documents;
  const exceptionReportDocuments =
    documentsData?.processedManifests?.items?.[0]?.exceptionReportSubmission?.documents;

  // Check for unresolved 45-day compliance discrepancy to show proof-of-contact button
  const { data: discrepancyData } = useQuery(BULK_MANIFEST_DISCREPANCIES_QUERY, {
    variables: { processedManifestIds: processedManifestId ? [processedManifestId] : [] },
    skip: !processedManifestId || !showCompliance,
  });
  const complianceInfo = useMemo(() => {
    const discrepancies = discrepancyData?.bulkManifestDiscrepancies ?? [];
    return {
      hasUnresolved45Day: discrepancies.some((d) => d.escalation === "OVERDUE_45" && !d.resolvedAt),
      hasUnresolved60Day: discrepancies.some((d) => d.escalation === "OVERDUE_60" && !d.resolvedAt),
    };
  }, [discrepancyData]);

  const hasProofOfContact = useMemo(
    () =>
      manifestDocuments?.some(
        (doc) => doc.document.documentType === DocumentType.WasteTransporterContactProof
      ) ?? false,
    [manifestDocuments]
  );

  const [showEditManifestDialog, setShowEditManifestDialog] = useState(false);
  const [showDiscrepancyDialog, setShowDiscrepancyDialog] = useState(false);
  const [showProofOfContactDialog, setShowProofOfContactDialog] = useState(false);
  const [showAttachDialog, setShowAttachDialog] = useState(false);
  const [addEditDialogState, setAddEditDialogState] = useState<AddEditDocumentDialogState>(
    defaultAddEditDocumentDialogState
  );
  const [detachDocumentId, setDetachDocumentId] = useState<string | null>(null);
  const [detaching, setDetaching] = useState(false);
  const [getDownloadLink] = useDocumentDownloadLink();
  const [previewState, setPreviewState] = useState<{
    open: boolean;
    fileUrl: string;
    fileExtension: string;
    title: string;
  }>({ open: false, fileUrl: "", fileExtension: "", title: "" });

  const handlePreview = useCallback(
    async (doc: { id: string; title: string; fileExtension?: string | null }) => {
      const { data } = await getDownloadLink({ variables: { id: doc.id } });
      if (data?.getDocumentDownloadLink) {
        setPreviewState({
          open: true,
          fileUrl: data.getDocumentDownloadLink,
          fileExtension: doc.fileExtension ?? "",
          title: doc.title,
        });
      }
    },
    [getDownloadLink]
  );

  const [attachDocumentToManifest] = useMutation(ATTACH_DOCUMENT_TO_MANIFEST, {
    refetchQueries: [PROCESSED_MANIFEST_DOCUMENTS_QUERY],
  });
  const [detachDocumentFromManifest] = useMutation(DETACH_DOCUMENT_FROM_MANIFEST, {
    refetchQueries: [PROCESSED_MANIFEST_DOCUMENTS_QUERY],
  });
  const [createExceptionReport, { loading: creatingExceptionReport }] = useMutation(
    CREATE_EXCEPTION_REPORT_SUBMISSION,
    {
      refetchQueries: [PROCESSED_MANIFESTS_QUERY],
      onCompleted: () => alerts.success("Exception report submission created"),
      onError: (err) => alerts.error(`Failed to create exception report: ${err.message}`),
    }
  );
  const { dataSourcingNote, withAttribution } = useMemo(() => {
    if (manifest?.vendorManifest && manifest?.isEManifest) {
      return {
        dataSourcingNote: "All manifest info is from vendor data unless otherwise noted",
        withAttribution: (label: string) => {
          // I realize that this is actually always Vendor, but it's because both are present
          // and we don't do field-wise coalescing, rather, we pick vendor for the whole set of fields.
          return `${label} (Vendor)`;
        },
      };
    } else if (manifest?.vendorManifest) {
      return {
        dataSourcingNote: "All manifest info is from vendor data",
        withAttribution: (label: string) => label,
      };
    } else if (manifest?.isEManifest) {
      return {
        dataSourcingNote: "All manifest info is from e‑Manifest data",
        withAttribution: (label: string) => label,
      };
    }
    return {
      dataSourcingNote: "",
      withAttribution: (label: string) => label,
    };
  }, [manifest]);

  const daysInTransitFields = useMemo(() => {
    if (!manifest) return null;

    return getDaysInTransitInfo(manifest.eManifestDaysInTransit, manifest.receivedDate, theme);
  }, [manifest, theme]);

  return (
    <>
      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="h5">
          {manifest
            ? `Manifest #${
                manifest.vendorManifest
                  ? manifest.vendorManifest?.manifestTrackingNumber
                  : manifest.manifestTrackingNumber
              }`
            : "Not Found"}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {isStaff && manifest?.vendorManifest && (
            <>
              {(manifest.vendorManifest?.manuallyEdited ||
                wasteLinesUnified?.wasteLinesUnified?.items.some(
                  (line) => line.manuallyEdited
                )) && (
                <Typography variant="caption" color="textDisabled" fontStyle="italic">
                  edited
                </Typography>
              )}
              <Button
                startIcon={<EditIcon />}
                color="warning"
                variant="text"
                size="large"
                disabled={!manifest}
                onClick={() => setShowEditManifestDialog(true)}
              >
                Edit Manifest
              </Button>
              {showEditManifestDialog && manifest && (
                <EditManifestDialog
                  onClose={() => setShowEditManifestDialog(false)}
                  manifest={manifest}
                  onDeleteSuccess={() => {
                    setShowEditManifestDialog(false);
                    onClose();
                  }}
                />
              )}
            </>
          )}
          {showSourceDocument && manifest && (
            <ManifestDocumentActions manifest={manifest} tenantId={tenantId ?? ""} />
          )}
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <Stack spacing={3} px={3} py={2} overflow="auto">
        <Typography variant="body1" color="text.secondary">
          {dataSourcingNote}
        </Typography>

        {showDiscrepancyCounts &&
          manifest &&
          (manifest.unresolvedDiscrepancyCount ?? 0) + (manifest.pendingDiscrepancyCount ?? 0) >
            0 && (
            <Alert
              severity={(manifest.unresolvedDiscrepancyCount ?? 0) > 0 ? "warning" : "info"}
              variant="outlined"
              sx={{
                cursor: "pointer",
                // Vertically center the severity icon with the row of chips + text
                alignItems: "center",
                "& .MuiAlert-icon": { alignItems: "center", py: 0 },
              }}
              onClick={() => setShowDiscrepancyDialog(true)}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                {(manifest.unresolvedDiscrepancyCount ?? 0) > 0 && (
                  <Chip
                    label={`${manifest.unresolvedDiscrepancyCount} unresolved`}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )}
                {(manifest.pendingDiscrepancyCount ?? 0) > 0 && (
                  <Chip
                    label={`${manifest.pendingDiscrepancyCount} pending`}
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                )}
                <Typography variant="body2">
                  {(manifest.unresolvedDiscrepancyCount ?? 0) +
                    (manifest.pendingDiscrepancyCount ?? 0) ===
                  1
                    ? "discrepancy"
                    : "discrepancies"}{" "}
                  between{" "}
                  {manifest.vendorManifest != null && manifest.documentManifestId != null
                    ? "e-Manifest, vendor, and document"
                    : manifest.documentManifestId != null
                      ? "e-Manifest and document"
                      : "e-Manifest and vendor"}
                </Typography>
              </Stack>
            </Alert>
          )}
        {showDiscrepancyDialog && manifest && (
          <DiscrepancyReviewDialog
            processedManifestIds={[manifest.id]}
            manifestTrackingNumber={
              manifest.vendorManifest?.manifestTrackingNumber ?? manifest.manifestTrackingNumber
            }
            onClose={() => setShowDiscrepancyDialog(false)}
          />
        )}

        <Grid container spacing={2}>
          <Grid size={4}>
            <InfoBox text={manifest?.facility?.name} label="Facility Name" />
          </Grid>
          <Grid size={4}>
            <InfoBox
              text={
                manifest
                  ? formatAddress(
                      manifest.facility?.streetAddress1,
                      manifest.facility?.city,
                      manifest.facility?.state,
                      manifest.facility?.zip
                    )
                  : ""
              }
              label="Facility Address"
            />
          </Grid>
          <Grid size={4}>
            <InfoBox text={manifest?.epaId} label="EPA ID" />
          </Grid>
          <Grid size={4}>
            <InfoBox
              text={
                manifest
                  ? getUtcDisplayDate(
                      manifest.vendorManifest
                        ? manifest.vendorManifest?.shippedDate
                        : manifest.shippedDate
                    )
                  : ""
              }
              label={withAttribution("Ship Date")}
              renderValue={(value) => (
                <ValueWithStaffEditedNotation
                  value={value}
                  isEdited={
                    !!manifest?.vendorManifest &&
                    manifest.vendorManifest?.originalShippedDate != null &&
                    manifest.vendorManifest?.shippedDate !==
                      manifest.vendorManifest?.originalShippedDate
                  }
                />
              )}
            />
          </Grid>
          <Grid size={4}>
            <InfoBox
              text={manifest?.vendorManifest?.vendorSource}
              label="Vendor"
              renderValue={(value) => (
                <ValueWithStaffEditedNotation
                  value={value}
                  isEdited={
                    !!manifest?.vendorManifest &&
                    manifest.vendorManifest?.originalVendorSource != null &&
                    manifest.vendorManifest?.vendorSource !==
                      manifest.vendorManifest?.originalVendorSource
                  }
                />
              )}
            />
          </Grid>
          <Grid size={4}>
            <InfoBox
              text={
                manifest?.vendorManifest
                  ? manifest.vendorManifest?.desFacilityName
                  : manifest?.desFacilityName
              }
              label={withAttribution("TSDF")}
              renderValue={(value) => (
                <ValueWithStaffEditedNotation
                  value={value}
                  isEdited={
                    !!manifest?.vendorManifest &&
                    manifest.vendorManifest?.originalDesFacilityName != null &&
                    manifest.vendorManifest?.desFacilityName !==
                      manifest.vendorManifest?.originalDesFacilityName
                  }
                />
              )}
            />
          </Grid>
          <Grid size={4}>
            <InfoBox
              text={(manifest?.vendorManifest ?? manifest)?.desFacilityId}
              label={withAttribution("TSDF EPA ID")}
            />
          </Grid>
          <Grid size={4}>
            {(() => {
              const tsdfSource = manifest?.vendorManifest ?? manifest;
              return (
                <InfoBox
                  text={formatAddress(
                    tsdfSource?.desFacLocationStreet1,
                    tsdfSource?.desFacLocationCity,
                    tsdfSource?.desFacLocationState,
                    tsdfSource?.desFacLocationZip
                  )}
                  label={withAttribution("TSDF Address")}
                />
              );
            })()}
          </Grid>
          {/* Only show Received by TSDF for regulated e-Manifests */}
          {manifest?.isRegulatedEManifest && (
            <Grid size={4}>
              <InfoBox
                text={manifest?.receivedDate ? getUtcDisplayDate(manifest.receivedDate) : undefined}
                label="Received by TSDF"
                renderValue={(value) =>
                  value ? (
                    value
                  ) : (
                    <Typography fontStyle="italic" color="text.secondary" variant="body1">
                      Pending
                    </Typography>
                  )
                }
              />
            </Grid>
          )}
          {daysInTransitFields && (
            <Grid size={4}>
              <DisplayFieldWithTooltip
                text={daysInTransitFields.display}
                textColor={daysInTransitFields.color}
                isItalic={!manifest?.receivedDate}
                label="Days In Transit"
                tooltip={daysInTransitFields.tooltipText}
                tooltipEnterDelay={500}
                tooltipPlacement="right"
              />
            </Grid>
          )}
        </Grid>

        {manifest?.wasteTransporters && manifest.wasteTransporters.length > 0 && (
          <Stack spacing={1}>
            <Typography variant="subtitle2" color="text.secondary">
              Transporters
            </Typography>
            <DataGrid
              rows={manifest.wasteTransporters}
              columns={transporterColumns}
              getRowId={(row) => row.transporterLineNumber}
              disableRowSelectionOnClick
              hideFooter
              autoHeight
            />
          </Stack>
        )}

        {showCompliance &&
          complianceInfo.hasUnresolved45Day &&
          !hasProofOfContact &&
          !manifest?.transporterContactedAt &&
          !manifest?.isEManifest &&
          manifest?.shippedDate && (
            <Alert
              severity={complianceInfo.hasUnresolved60Day ? "error" : "warning"}
              variant="outlined"
            >
              <Stack spacing={1}>
                <Typography variant="body2" fontWeight={500}>
                  e-Manifest upload overdue —{" "}
                  {Math.floor(
                    (Date.now() - new Date(manifest.shippedDate).getTime()) / (24 * 60 * 60 * 1000)
                  )}{" "}
                  days since shipment
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Per 40 CFR 262.42(a), a large quantity generator who has not received a copy of
                  the manifest with the handwritten signature of the owner or operator of the
                  designated facility within 45 days of the date the waste was accepted by the
                  initial transporter must contact the transporter and/or the owner or operator of
                  the designated facility to determine the status of the hazardous waste. Small
                  quantity generators are also encouraged to contact the transporter to avoid the
                  60-day exception report deadline. If confirmation is not received within 60 days,
                  both large and small quantity generators must submit an Exception Report to the
                  EPA e-Manifest system.
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AttachFile />}
                  onClick={() => setShowProofOfContactDialog(true)}
                  sx={{ alignSelf: "flex-start" }}
                >
                  Add Proof of Contact
                </Button>
              </Stack>
            </Alert>
          )}

        {showExceptionReporting && manifest?.exceptionReportSubmission && (
          <Alert
            severity={
              manifest.exceptionReportSubmission.status === SubmissionStatus.FilingComplete
                ? "info"
                : "warning"
            }
            variant="outlined"
          >
            <Typography variant="body2">
              {manifest.exceptionReportSubmission.status === SubmissionStatus.FilingComplete
                ? "Exception report filed —"
                : "Exception report started —"}{" "}
              <Link
                component={RouterLink}
                to={`/o/${tenantId}/waste/submissions/${manifest.exceptionReportSubmission.id}`}
              >
                {manifest.exceptionReportSubmission.title}
              </Link>
            </Typography>
          </Alert>
        )}

        {showExceptionReporting &&
          !manifest?.exceptionReportSubmission &&
          complianceInfo.hasUnresolved60Day &&
          manifest && (
            <Alert severity="error" variant="outlined">
              <Stack spacing={1}>
                <Typography variant="body2" fontWeight={500}>
                  Exception report required — 60+ days since shipment with no e-Manifest upload
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No exception report submission has been created for this manifest yet.
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  disabled={creatingExceptionReport}
                  onClick={() =>
                    createExceptionReport({
                      variables: { processedManifestId: manifest.id },
                    })
                  }
                  sx={{ alignSelf: "flex-start" }}
                >
                  {creatingExceptionReport ? "Creating…" : "Create Exception Report"}
                </Button>
              </Stack>
            </Alert>
          )}

        {showProofOfContactDialog && manifest && (
          <ProofOfContactDialog
            open={showProofOfContactDialog}
            onClose={() => setShowProofOfContactDialog(false)}
            processedManifestId={manifest.id}
          />
        )}

        {showDiscrepancyCounts && (
          <>
            {/* Documents Section */}
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2" color="text.secondary">
                  Documents
                </Typography>
                {manifest && (
                  <Button
                    startIcon={<AttachFile />}
                    size="small"
                    onClick={() => {
                      setShowAttachDialog(true);
                      setAddEditDialogState({
                        ...defaultAddEditDocumentDialogState,
                        open: true,
                        mode: EditMode.ADD,
                        step: FormState.UPLOAD,
                      });
                    }}
                  >
                    Attach Document
                  </Button>
                )}
              </Stack>

              {documentsError ? (
                <Typography variant="body2" color="error">
                  Failed to load documents
                </Typography>
              ) : manifestDocuments && manifestDocuments.length > 0 ? (
                <Stack spacing={2}>
                  {(() => {
                    const groupLabels = new Map<string, string>();
                    const groups = new Map<string, NonNullable<typeof manifestDocuments>>();
                    for (const doc of manifestDocuments) {
                      const isKnownWasteType = WASTE_DOCUMENT_TYPES.has(doc.document.documentType);
                      const key = isKnownWasteType ? doc.document.documentType : "OTHER";
                      if (!groupLabels.has(key)) {
                        groupLabels.set(
                          key,
                          isKnownWasteType
                            ? documentTypeToLabel(doc.document.documentType)
                            : "Other"
                        );
                      }
                      const existing = groups.get(key) ?? [];
                      existing.push(doc);
                      groups.set(key, existing);
                    }
                    return [...groups.entries()].map(([groupKey, docsOfType]) => (
                      <Stack key={groupKey} spacing={1}>
                        <Chip
                          label={groupLabels.get(groupKey) ?? "Other"}
                          size="small"
                          variant="outlined"
                          sx={{ alignSelf: "flex-start" }}
                        />
                        {docsOfType.map((manifestDoc) => (
                          <Stack
                            key={manifestDoc.id}
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{
                              py: 0.5,
                              px: 1,
                              borderRadius: 1,
                              "&:hover": { bgcolor: "action.hover" },
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
                              <AttachFile fontSize="small" color="action" />
                              <Typography variant="body2" noWrap>
                                {manifestDoc.document.title}
                              </Typography>
                              {manifestDoc.document.fileExtension && (
                                <Chip
                                  label={manifestDoc.document.fileExtension.toUpperCase()}
                                  size="small"
                                  variant="outlined"
                                  sx={{ flexShrink: 0 }}
                                />
                              )}
                            </Stack>
                            <Stack direction="row" spacing={0.5} flexShrink={0}>
                              {isPreviewable(manifestDoc.document.fileExtension) && (
                                <IconButton
                                  size="small"
                                  onClick={() => handlePreview(manifestDoc.document)}
                                  title="Preview"
                                >
                                  <Visibility fontSize="small" />
                                </IconButton>
                              )}
                              <IconButton
                                size="small"
                                onClick={async () => {
                                  await fetchAndHandleDownload({
                                    documentId: manifestDoc.document.id,
                                  });
                                  clearDownloadLink();
                                }}
                                title="Download"
                              >
                                <Download fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => setDetachDocumentId(manifestDoc.id)}
                                title="Detach"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Stack>
                        ))}
                      </Stack>
                    ));
                  })()}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                  No documents attached
                </Typography>
              )}
            </Stack>

            {showExceptionReporting &&
              exceptionReportDocuments &&
              exceptionReportDocuments.length > 0 && (
                <Stack spacing={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Exception Report Documents
                  </Typography>
                  {exceptionReportDocuments.map((subDoc) => (
                    <Stack
                      key={subDoc.id}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        py: 0.5,
                        px: 1,
                        borderRadius: 1,
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
                        <AttachFile fontSize="small" color="action" />
                        <Typography variant="body2" noWrap>
                          {subDoc.document.title}
                        </Typography>
                        {subDoc.document.fileExtension && (
                          <Chip
                            label={subDoc.document.fileExtension.toUpperCase()}
                            size="small"
                            variant="outlined"
                            sx={{ flexShrink: 0 }}
                          />
                        )}
                      </Stack>
                      <Stack direction="row" spacing={0.5} flexShrink={0}>
                        {isPreviewable(subDoc.document.fileExtension) && (
                          <IconButton
                            size="small"
                            onClick={() => handlePreview(subDoc.document)}
                            title="Preview"
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={async () => {
                            await fetchAndHandleDownload({
                              documentId: subDoc.document.id,
                            });
                            clearDownloadLink();
                          }}
                          title="Download"
                        >
                          <Download fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              )}

            <ConfirmDialog
              open={detachDocumentId != null}
              header="Detach document"
              info="Are you sure you want to detach this document from the manifest?"
              confirmText="Yes, Detach"
              loading={detaching}
              onClose={() => setDetachDocumentId(null)}
              onConfirm={async () => {
                if (detachDocumentId == null) return;
                setDetaching(true);
                try {
                  await detachDocumentFromManifest({
                    variables: {
                      processedManifestDocumentId: detachDocumentId,
                    },
                  });
                  alerts.success("Document detached from manifest");
                  setDetachDocumentId(null);
                } catch (err) {
                  alerts.error("Failed to detach document");
                } finally {
                  setDetaching(false);
                }
              }}
            />

            {showAttachDialog && tenantId && manifest && (
              <Stack spacing={1} sx={{ mb: 1 }}>
                <AddEditDocumentDialog
                  tenantId={tenantId}
                  dialogState={addEditDialogState}
                  setDialogState={(stateOrUpdater) => {
                    setAddEditDialogState(stateOrUpdater);
                    // If dialog is being closed, also close the attach flow
                    if (typeof stateOrUpdater === "object" && !stateOrUpdater.open) {
                      setShowAttachDialog(false);
                    }
                  }}
                  documentTypeOptions={[
                    DocumentType.WasteTransporterContactProof,
                    DocumentType.WasteExceptionReportProof,
                    DocumentType.Other,
                  ]}
                  showFacilityPicker={false}
                  onSubmit={async (document) => {
                    try {
                      await attachDocumentToManifest({
                        variables: {
                          processedManifestId: manifest.id,
                          documentId: document.id,
                        },
                      });
                      alerts.success("Document attached to manifest");
                      setShowAttachDialog(false);
                    } catch (err) {
                      alerts.error("Failed to attach document to manifest");
                    }
                  }}
                  refetchQueries={[]}
                />
              </Stack>
            )}
          </>
        )}

        {children}
      </Stack>

      <FullScreenDocumentPreview
        open={previewState.open}
        onClose={() => setPreviewState((prev) => ({ ...prev, open: false }))}
        fileUrl={previewState.fileUrl}
        fileExtension={previewState.fileExtension}
        title={previewState.title}
      />
    </>
  );
};

export const ManifestDetailSectionSkeleton = () => {
  return (
    <>
      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Skeleton variant="text" width={300} height={40} />
        <Stack direction="row" spacing={1} alignItems="center">
          <Skeleton variant="text" width={60} height={24} />
          <Skeleton variant="rectangular" width={120} height={36} />
          <Skeleton variant="circular" width={32} height={32} />
        </Stack>
      </Box>

      <Stack spacing={3} px={3} py={2} overflow="auto">
        <Skeleton variant="text" width={400} height={24} />

        <Grid container spacing={2}>
          <Grid size={4}>
            <Skeleton variant="text" width={200} height={32} />
          </Grid>
          <Grid size={4}>
            <Skeleton variant="text" width={200} height={32} />
          </Grid>
          <Grid size={4}>
            <Skeleton variant="text" width={200} height={32} />
          </Grid>
          <Grid size={4}>
            <Skeleton variant="text" width={200} height={32} />
          </Grid>
          <Grid size={4}>
            <Skeleton variant="text" width={200} height={32} />
          </Grid>
          <Grid size={4}>
            <Skeleton variant="text" width={200} height={32} />
          </Grid>
        </Grid>

        <ManifestLineItemDetailSectionSkeleton />
      </Stack>
    </>
  );
};
